import { useRef, useState, type FormEvent } from "react";

const inquiryTopics = [
  "眼部療程（眼袋／淚溝／雙眼皮）",
  "抗衰老療程（輪廓／拉提／頭皮）",
  "皮膚療程（斑／痣／疣）",
  "祛紋及疤療程（妊娠紋／頸紋／疤痕）",
  "其他皮膚問題",
];

export default function TreatmentInquiryForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("mobile_alt") as string) || "";
    if (honeypot.trim().length > 0) {
      setStatus("success");
      form.reset();
      return;
    }
    data.delete("mobile_alt");
    const email = (data.get("email") as string) || "";
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setErrorMsg("請輸入有效的電子郵件地址");
      return;
    }
    setStatus("loading");
    try {
      const params = new URLSearchParams();
      data.forEach((v, k) => params.append(k, String(v)));
      const res = await fetch("https://readdy.ai/api/form/dahsno3p14a1h3mr6sg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(text);
      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "送出失敗，請稍後再試");
      }
    } catch {
      setStatus("error");
      setErrorMsg("網路錯誤，請稍後再試");
    }
  };

  return (
    <section id="inquiry" className="py-16 md:py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="inline-block px-6 py-2 rounded-md bg-brand-600 text-white text-lg tracking-wider">療程查詢</div>
          <h2 className="mt-5 font-serif text-3xl md:text-4xl text-brand-800">留低資料，專人為你評估</h2>
          <p className="mt-4 text-brand-700/70 text-sm md:text-base">
            唔想即時開 WhatsApp？填妥以下資料，我們的專科顧問會盡快與你聯絡，為你解答皮膚與紋路問題。
          </p>
        </div>

        <form
          id="treatment-inquiry-form"
          ref={formRef}
          onSubmit={onSubmit}
          data-readdy-form
          className="bg-brand-50/60 border border-brand-100 rounded-lg p-6 md:p-8 space-y-5"
        >
          <input
            type="text"
            name="mobile_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="hp-honeypot"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="inquiry-name" className="block text-sm font-medium text-brand-900 mb-1.5">姓名 *</label>
              <input
                id="inquiry-name"
                type="text"
                name="name"
                required
                placeholder="你的姓名"
                className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="inquiry-phone" className="block text-sm font-medium text-brand-900 mb-1.5">聯絡電話 *</label>
              <input
                id="inquiry-phone"
                type="tel"
                name="phone"
                required
                placeholder="你的電話號碼"
                className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>

          <div>
            <label htmlFor="inquiry-email" className="block text-sm font-medium text-brand-900 mb-1.5">電郵地址 *</label>
            <input
              id="inquiry-email"
              type="email"
              name="email"
              required
              placeholder="your@email.com"
              className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div>
            <label htmlFor="inquiry-topic" className="block text-sm font-medium text-brand-900 mb-1.5">想查詢的療程 *</label>
            <select
              id="inquiry-topic"
              name="topic"
              required
              defaultValue=""
              className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            >
              <option value="" disabled>請選擇想查詢的療程</option>
              {inquiryTopics.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="inquiry-message" className="block text-sm font-medium text-brand-900 mb-1.5">想了解的問題（選填）</label>
            <textarea
              id="inquiry-message"
              name="message"
              rows={4}
              maxLength={500}
              placeholder="請簡單描述你的皮膚或紋路困擾（最多 500 字）"
              className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            ></textarea>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm whitespace-nowrap cursor-pointer transition disabled:opacity-60"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> 送出中...
                </>
              ) : (
                <>
                  提交查詢 <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>

            {status === "success" && (
              <p className="mt-3 text-sm text-brand-600 text-center">查詢已送出！我們的專科顧問會盡快與你聯絡。</p>
            )}
            {status === "error" && (
              <p className="mt-3 text-sm text-rose-500 text-center">{errorMsg}</p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}